import React, { Fragment, Suspense, useEffect, useState, useRef, useMemo } from "react";

import {
  deletePins,
  getMaxPinDate,
  getMinPinDate,
  getPin,
  getPins,
} from "../../actions/pins";
import { searchUsers } from "../../actions/users";
import { useDispatch, useSelector } from "react-redux";
import useRemovalConfirm from "../profile/CustomHooks/useRemovalConfirm";
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import ConfirmationModal from "../profile/ConfirmationModal";

import LeafletMap from "./LeafletMap";
import StorySidebar from "../layout/StorySidebar";
import AIFloatingButton from '../chat/AIFloatingButton';

import TutorialButton from "../tutorial/TutorialButton"; // import tutorial button
import SearchTutorial from "../tutorial/SearchTutorial"; // import search tutorial

const SearchSidebar = React.lazy(() => import("../layout/SearchSidebar"));

export default function MapDashboard() {
  const pins = useSelector((state) => state.pins.pins);
  const dispatch = useDispatch();
  const [isSearch, setIsSearch] = useState(false);

  const [isTutorialOpen, setIsTutorialOpen] = useState(false); // tutorial state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("search"); // sidebar tab tracker (search or add)
  const [closeStorySignal, setCloseStorySignal] = useState(0);
  const [tutorialMode, setTutorialMode] = useState(false);

  const mapReference = useRef(null);
  const history = useHistory();

  const [suggestedStory, setSuggestedStory] = useState(null);

  useEffect(() => {
    dispatch(getMaxPinDate());
    dispatch(getMinPinDate());
    dispatch(searchUsers(""));
    dispatch(getPins());
  }, []);

  const centerMarker = (marker) => {
    const lat = Number(marker.latitude);
    const lng = Number(marker.longitude);
    if (mapReference.current && !(isNaN(lat) || isNaN(lng))) {
      mapReference.current.leafletElement.flyTo(
        [lat, lng],
        mapReference.current.leafletElement.getZoom(),
      );
    }
  };

  // tutorial start
  const startTutorial = () => {
    setTutorialMode(true);
    setCloseStorySignal((n) => n + 1); // signal to close story sidebar if open
    setActiveTab("search");
    setSidebarOpen(true);
    setIsTutorialOpen(true);
  };

  // tutorial end
  const endTutorial = () => {
    setIsTutorialOpen(false);

    setActiveTab("search");
    setSidebarOpen(true);

    requestAnimationFrame(() => {
      const sidebar = document.getElementById("sidebar-style");
      if (sidebar) sidebar.scrollTop = 0;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTutorialMode(false);
      });
    });
  };

  // Method to handle drop pin
  const handleDropPin = (locationData) => {
    setDroppedLocation(locationData);
    setDroppedMarker(locationData.marker);
    setShouldOpenAddStory(true);
  };

  const clearDroppedMarker = () => {
    if (droppedMarker) {
      droppedMarker.remove();
      setDroppedMarker(null);
    }
    setDroppedLocation(null);
    setShouldOpenAddStory(false);
  };

  const handleSearchLocation = (locationData) => {
    setSearchLocation(locationData);
    setIsSearch(true);
  };

  const handleClearSearchLocation = () => {
    setSearchLocation(null);
    setIsSearch(false);
  };

  const handleSuggestStory = (storyData) => {
    setSuggestedStory(storyData);
    setShouldOpenAddStory(true);

    if (history.location.pathname !== '/') {
      history.push('/');
    }

    if (storyData.latitude && storyData.longitude && mapReference.current) {
      mapReference.current.leafletElement.flyTo([storyData.latitude, storyData.longitude], 13);
    }
  };

  const handleNavigate = (locationData) => {
    if (locationData.latitude && locationData.longitude && mapReference.current) {
      const map = mapReference.current.leafletElement;
      map.flyTo([locationData.latitude, locationData.longitude], 15);

      const L = require('leaflet');
      const circle = L.circle([locationData.latitude, locationData.longitude], {
        color: '#667eea', fillColor: '#667eea', fillOpacity: 0.2, radius: 80, weight: 3,
      }).addTo(map);
      setTimeout(() => map.removeLayer(circle), 5000);

      if (locationData.pinId) {
        setTimeout(() => history.push(`/story/${locationData.pinId}`), 300);
      }
    } else if (locationData.pinId) {
      history.push(`/story/${locationData.pinId}`);
    }
  };

  const onDelProfile = () => {
    dispatch(deletePins(removalFav));
    setPinDeleted(true);
    dispatch(getMinPinDate());
    dispatch(getMaxPinDate());
  };

  const {
    removalModalState,
    removalToggle,
    onDeleteHome,
    removalFav,
    loginToggle,
    setloginregisterModalState,
    loginregisterModalState,
  } = useRemovalConfirm(onDelProfile);

  const auth = useSelector((state) => state.auth);
  const { isAuthenticated, user } = auth;

  const [addAddress, setAddAddress] = useState(false);
  const [droppedLocation, setDroppedLocation] = useState(null);
  const [shouldOpenAddStory, setShouldOpenAddStory] = useState(false);
  const [droppedMarker, setDroppedMarker] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [pinDeleted, setPinDeleted] = useState(false);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const filteredMapPins = useMemo(() => {
    console.log('🔍 Filtering pins:', { totalPins: pins.length, searchLocation, isFiltering: searchLocation !== null });

    if (!searchLocation) {
      console.log('✅ No filter, returning all pins:', pins.length);
      // filter out pins flagged by the user
      return pins.filter(pin => {
        return !pin.flaggerstory?.some(flag => flag.flagger === user?.id);
      });
    }

    const { latitude, longitude } = searchLocation;
    const result = pins.filter(pin => {
      if (latitude && longitude && pin.latitude && pin.longitude) {
        return calculateDistance(latitude, longitude, Number(pin.latitude), Number(pin.longitude)) < 16;
      }
      return false;
    });

    console.log('✅ Filtered result:', result.length, 'pins');
    return result;
  }, [pins, searchLocation, user]);

  const isFiltering = searchLocation !== null;
  const minPinDate = useSelector((state) => state.pins.pinMinDate);
  const maxPinDate = useSelector((state) => state.pins.pinMaxDate);

  return (
    <Fragment>
      <Switch>
        <Route path="/">
          <div id={"map-dashboard"}>
            <TutorialButton onClick={startTutorial} /> {/* tutorial button */}

            {/* tutorial component */}
            <SearchTutorial 
              open={isTutorialOpen} 
              onClose={endTutorial}
              setActiveTab={setActiveTab}
            />
              
            <div id={"sidebar-style"}>
              <Suspense fallback={<div>Loading...</div>}>
                <SearchSidebar
                  maplink={"/story"}
                  setAddAddress={setAddAddress}
                  addAddress={addAddress}
                  loginToggle={loginToggle}
                  maxPinDate={maxPinDate}
                  minPinDate={minPinDate}
                  setloginregisterModalState={setloginregisterModalState}
                  centerMarker={centerMarker}
                  isSearch={isSearch}
                  setIsSearch={setIsSearch}
                  // Handle drop pin
                  droppedLocation={droppedLocation}  
                  shouldOpenAddStory={shouldOpenAddStory}  
                  setShouldOpenAddStory={setShouldOpenAddStory}  
                  searchLocation={searchLocation}  
                  setSearchLocation={setSearchLocation}  
                  droppedMarker={droppedMarker}  
                  clearDroppedMarker={clearDroppedMarker}  
                  
                  isAuthenticated={isAuthenticated}

                  // tutorial mode
                  tutorialMode={tutorialMode}

                  // tutorial sidebar tracker
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}

                  // sidebar open/close for AI
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                  suggestedStory={suggestedStory}
                  setSuggestedStory={setSuggestedStory}
                />
              </Suspense>
              <Switch>
                <Route path="/story">
                  <StoryDisplayExp
                    maplink={"/story"}
                    loginToggle={loginToggle}
                    isAuthenticated={isAuthenticated}
                    user={user}
                    centerMarker={centerMarker}
                    removalToggle={removalToggle}
                    closeStorySignal={closeStorySignal}
                  />
                </Route>
              </Switch>
            </div>

            <LeafletMap
              maplink={"/story"}
              pins={filteredMapPins}
              mapReference={mapReference}
              user={user}
              isAuthenticated={isAuthenticated}
              centerMarker={centerMarker}
              onDropPin={handleDropPin}
              onSearchLocation={handleSearchLocation}
              onClearSearchLocation={handleClearSearchLocation}
              onClearDroppedMarker={clearDroppedMarker}
              droppedMarker={droppedMarker}
              isFiltering={isFiltering}
            />
          </div>

          <AIFloatingButton onSuggestStory={handleSuggestStory} onNavigate={handleNavigate} />
        </Route>
      </Switch>

      <ConfirmationModal
        modalState={removalModalState}
        toggle={removalToggle}
        onSubmit={onDeleteHome}
        title="Are you sure you want to delete this story?"
        buttonTitle={"Yes, delete this story"}
      />
      <ConfirmationModal
        modalState={loginregisterModalState}
        toggle={loginToggle}
        login={true}
        title="login or register"
      />
    </Fragment>
  );
}

function StoryDisplayExp(props) {
  let match = useRouteMatch();
  return (
    <Switch>
      <Route path={`${match.path}/:id`}>
        <IndividualStoryExp {...props} />
      </Route>
      <Route path={match.path}>
        <StorySidebar {...props} closeSignal={props.closeStorySignal} />
      </Route>
    </Switch>
  );
}

function IndividualStoryExp(props) {
  let { id } = useParams();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { isAuthenticated, user } = auth;
  const userid = isAuthenticated ? user.id : false;

  useEffect(() => {
    dispatch(getPin(id, userid));
  }, [id]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <StorySidebar
        {...props}
        closeSignal={props.closeStorySignal}
      />
    </Suspense>
  );
}